/**
 * ClientOnly 组件
 * 包裹只在客户端渲染的内容，避免 SSR 时报错
 *
 * 【使用场景】
 * - 访问 window、document 等浏览器 API
 * - 使用不支持 SSR 的第三方库
 * - 需要客户端特定的交互效果
 *
 * @example
 * <ClientOnly>
 *   <div>{{ window.innerWidth }}</div>
 * </ClientOnly>
 *
 * @example 带占位符
 * <ClientOnly placeholder="加载中...">
 *   <HeavyComponent />
 * </ClientOnly>
 */
import { defineComponent, ref, onMounted } from 'vue';

const ClientOnly = defineComponent({
  name: 'ClientOnly',
  props: {
    /**
     * 服务端占位文本
     */
    placeholder: {
      type: String,
      default: '',
    },
    /**
     * 包装元素标签名
     */
    tag: {
      type: String,
      default: 'div',
    },
  },
  setup(props, { slots }) {
    /**
     * 【响应式状态】
     * ref(false) 创建响应式引用，初始值为 false
     * 服务端和客户端初次渲染时都是 false
     * onMounted 只在客户端执行，将其设为 true
     */
    const isMounted = ref(false);

    /**
     * 【生命周期钩子】
     * onMounted 在组件挂载到 DOM 后执行
     * - 服务端：不执行（没有 DOM）
     * - 客户端：执行，isMounted 变为 true
     * 这就是实现"仅客户端渲染"的关键
     */
    onMounted(() => {
      isMounted.value = true;
    });

    /**
     * 【渲染函数】
     * setup 返回函数时，该函数作为组件的渲染函数
     * - !isMounted.value: 服务端或客户端首次渲染，显示占位
     * - isMounted.value: 客户端挂载后，显示实际内容
     */
    return () => {
      // 服务端：显示占位符或什么都不显示
      if (!isMounted.value) {
        if (slots.placeholder) return slots.placeholder();
        if (props.placeholder) return props.placeholder;
        return null;
      }

      // 客户端挂载后：显示实际内容
      if (slots.default) return slots.default();
      return null;
    };
  },
});

export default ClientOnly;
